import type { ReplayPresentationStep } from '../rendering/types';
import { formatCents } from './format';

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`Replay-Element fehlt: ${selector}`);
  return element;
}

export interface ReplayHeaderView {
  readonly name: string;
  readonly xValue: number;
  readonly totalWinCents: number;
}

export interface ReplayListItemView {
  readonly id: string;
  readonly name: string;
  readonly xValue: number;
  readonly createdAt: number;
}

export function formatReplayAwards(step: ReplayPresentationStep): string {
  const awards = [
    (step.retriggerAwarded ?? 0) > 0 ? `+${step.retriggerAwarded} Retrigger` : '',
    step.potOfGreed === undefined ? '' : '+1 Topf der Gier',
  ].filter(Boolean);
  return awards.join(' · ') || '—';
}

export class ReplayDialogView {
  private readonly dialog: HTMLDialogElement;
  private readonly name: HTMLElement;
  private readonly x: HTMLElement;
  private readonly total: HTMLElement;
  private readonly stepLabel: HTMLElement;
  private readonly spinWin: HTMLElement;
  private readonly scatter: HTMLElement;
  private readonly sticky: HTMLElement;
  private readonly award: HTMLElement;
  private readonly previous: HTMLButtonElement;
  private readonly next: HTMLButtonElement;
  private readonly auto: HTMLButtonElement;
  private readonly list: HTMLSelectElement;
  private busy = false;
  private currentIndex = 0;
  private totalSteps = 0;

  constructor(root: Document) {
    this.dialog = requireElement(root, '[data-replay-dialog]');
    this.name = requireElement(root, '[data-replay-name]');
    this.x = requireElement(root, '[data-replay-x]');
    this.total = requireElement(root, '[data-replay-total]');
    this.stepLabel = requireElement(root, '[data-replay-step]');
    this.spinWin = requireElement(root, '[data-replay-spin-win]');
    this.scatter = requireElement(root, '[data-replay-scatter]');
    this.sticky = requireElement(root, '[data-replay-sticky]');
    this.award = requireElement(root, '[data-replay-award]');
    this.previous = requireElement(root, '[data-replay-prev]');
    this.next = requireElement(root, '[data-replay-next]');
    this.auto = requireElement(root, '[data-replay-auto]');
    this.list = requireElement(root, '[data-replay-list]');
  }

  renderList(items: readonly ReplayListItemView[], selectedId?: string): void {
    this.list.replaceChildren();
    if (items.length === 0) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'Kein Replay verfügbar';
      this.list.append(option);
      this.list.disabled = true;
      return;
    }
    this.list.disabled = false;
    items.forEach((item) => {
      const option = document.createElement('option');
      option.value = item.id;
      option.textContent = `${item.name} · ${item.xValue.toFixed(2)}× · ${new Date(item.createdAt).toLocaleDateString('de-DE')}`;
      option.selected = item.id === selectedId;
      this.list.append(option);
    });
  }

  show(header: ReplayHeaderView): void {
    this.name.textContent = header.name;
    this.x.textContent = `${header.xValue.toLocaleString('de-DE', { maximumFractionDigits: 2 })}×`;
    this.total.textContent = `${formatCents(header.totalWinCents)} €`;
    if (!this.dialog.open) this.dialog.showModal();
  }

  /** Prevents overlapping reveal timelines when a user clicks rapidly. */
  setBusy(busy: boolean): void {
    this.busy = busy;
    this.previous.disabled = busy || this.currentIndex <= 0;
    this.next.disabled = busy || this.currentIndex >= this.totalSteps - 1;
  }

  renderStep(step: ReplayPresentationStep, index: number, total: number, autoplay: boolean): void {
    this.currentIndex = index;
    this.totalSteps = total;
    this.stepLabel.textContent = `${index + 1} / ${total}`;
    this.spinWin.textContent = `Spin: ${formatCents(step.totalWinCents)} €`;
    this.scatter.textContent = `Scatter: ${step.scatterCount}`;
    this.sticky.textContent = `Sticky: ${step.stickyAfter.length}`;
    this.award.textContent = `Bonus: ${formatReplayAwards(step)}`;
    this.previous.disabled = this.busy || autoplay || index <= 0;
    this.next.disabled = this.busy || autoplay || index >= total - 1;
    this.auto.setAttribute('aria-pressed', String(autoplay));
    this.auto.textContent = autoplay ? 'Auto stoppen' : 'Auto Replay';
  }

  close(): void { if (this.dialog.open) this.dialog.close(); }
}
